import { useRef, useCallback } from 'react';

/**
 * 统一音频播放器管理
 * 用单个 Audio 实例替代每次 new Audio()，避免内存泄漏和竞态
 *
 * 用法：
 *   const audio = useAudio();
 *   audio.play(src, onEnd);        // 播放，结束时回调 onEnd
 *   audio.pause();                // 暂停
 *   audio.stop();                 // 停止并归零
 */
export function useAudio() {
  const ref = useRef(null);

  const stop = useCallback(() => {
    if (ref.current) {
      ref.current.pause();
      ref.current.currentTime = 0;
      ref.current = null;
    }
  }, []);

  const play = useCallback((src, onEnd) => {
    // 先停掉上一个
    stop();
    const audio = new Audio(src);
    ref.current = audio;
    audio.play().catch(() => {});
    if (onEnd) {
      audio.onended = () => {
        onEnd();
        ref.current = null;
      };
    } else {
      audio.onended = () => { ref.current = null; };
    }
  }, [stop]);

  const pause = useCallback(() => {
    if (ref.current) ref.current.pause();
  }, []);

  return { play, pause, stop };
}
